import { useState, useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { uniqueId } from '@utils/uniqueId';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@components/ui';
import Modal from '@components/ui/Modal/Modal';
import AlertModal from '@components/ui/AlertModal/AlertModal';
import ProfileActionsMenu from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import type { ProfileAction } from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import { DraggableTabs } from '@components/shared/DraggableTabs/DraggableTabs';
import type { TabItem } from '@components/shared/DraggableTabs/types';
import { getEmployeeById, activateEmployee, deleteEmployee, provisionEmployeeAccess } from '@/services/employeeService';
import { setUserProfileImage, clearUserProfileImage } from '@/services/userService';
import { uploadFile } from '@/services/fileService';
import { getTenantById } from '@/services/tenantService';
import { getCommercialTerms } from '@/services/commercialTermService';
import { getTemplateSnapshot } from '@/services/formTemplateService';
import { useNotificationStore } from '@stores/notificationStore';
import { useAuthStore } from '@/stores/authStore';
import { useCountries } from '@/hooks/useCountries';
import { useBreadcrumbOverride } from '@stores/breadcrumbStore';
import type { FormTemplateTab } from '@/types/formTemplate';
import { toDraggableTabItems } from '@utils/tabHelpers';
import { toTitleCase } from '@utils/textCase';
import { useTranslation } from '@/hooks/useTranslation';
import ProfileCard from '@features/tenant-profile/ProfileCard';
import type { ProfileField } from '@features/tenant-profile/ProfileCard';
import TabFormsContent from '@features/tenant-profile/TabFormsContent';
import EmployeeDocumentsTab from '@features/documents/EmployeeDocumentsTab';
import EmployeeEditForm from './EmployeeEditForm';
import type { ResolvedTab, ResolvedFormEntry } from '@features/tenant-profile/types';
import type { EmployeeDto } from '@/types/employee';
import { WorkpayApiError } from '@lib/apiEnvelope';
import { AuditTrailModal } from '@components/shared/AuditTrailModal';
import { AUDIT_RESOURCE } from '@lib/auditResourceTypes';
import styles from './EmployeeProfileView.module.scss';

const DOCUMENTS_TAB_ID = '_documents';

// ============================================
// HELPERS
// ============================================

function getDisplayName(emp: EmployeeDto): string {
  return emp.preferredName || `${emp.firstName} ${emp.lastName}`;
}

function buildProfileFields(
  emp: EmployeeDto,
  countryName: string,
  employmentTypeName: string,
  t: (key: string, fallback: string) => string,
  tenantName?: string,
): ProfileField[] {
  const fields: ProfileField[] = [];
  if (tenantName !== undefined) {
    fields.push({ label: t('K_LABEL_TENANT', 'Client'), value: tenantName || '-' });
  }
  fields.push(
    { label: t('K_LABEL_FIRST_NAME', 'First Name'), value: emp.firstName || '-' },
    { label: t('K_LABEL_LAST_NAME', 'Last Name'), value: emp.lastName || '-' },
    { label: t('K_LABEL_PREFERRED_NAME', 'Preferred Name'), value: emp.preferredName || '-' },
    { label: t('K_LABEL_WORK_EMAIL', 'Work Email'), value: emp.workEmail || '-' },
    { label: t('K_LABEL_COUNTRY', 'Country'), value: countryName || '-' },
    { label: t('K_LABEL_LEGAL_ENTITY', 'Legal Entity'), value: emp.legalEntity?.name || '-' },
    { label: t('K_LABEL_JOB_TITLE', 'Job Title'), value: emp.jobTitle?.name ?? '-' },
    { label: t('K_LABEL_DEPARTMENT', 'Department'), value: emp.department?.name ?? '-' },
    { label: t('K_LABEL_EMPLOYMENT_TYPE', 'Employment Type'), value: employmentTypeName || '-' },
    { label: t('K_LABEL_CONTRACT_TYPE', 'Contract Type'), value: emp.contractType || '-' },
    { label: t('K_LABEL_START_DATE', 'Start Date'), value: emp.startDate ? new Date(emp.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '-' },
    { label: t('K_LABEL_GROSS_SALARY', 'Gross Salary'), value: emp.grossSalary != null ? `${emp.salaryCurrency} ${emp.grossSalary.toLocaleString()}` : '-' },
    { label: t('K_LABEL_SALARY_FREQUENCY', 'Salary Frequency'), value: emp.salaryFrequency || '-' },
    { label: t('K_LABEL_PROBATION_PERIOD', 'Probation Period'), value: emp.probationMonths != null ? `${emp.probationMonths} months` : '-' },
    { label: t('K_LABEL_WEEKLY_HOURS', 'Weekly Hours'), value: emp.weeklyWorkingHours != null ? String(emp.weeklyWorkingHours) : '-' },
    { label: t('K_LABEL_STATUS', 'Status'), value: emp.status || '-' },
  );
  return fields;
}

function toResolvedTabs(apiTabs: FormTemplateTab[], overrideKey?: string): ResolvedTab[] {
  return apiTabs
    .map((tab) => ({
      _tabClientId: uniqueId('tab'),
      name: tab.name,
      description: tab.description ?? '',
      order: tab.order,
      forms: (tab.forms ?? []).map((form): ResolvedFormEntry => ({
        formConfigId: form.formConfigId,
        formName: form.formName ?? form.formConfigId,
        accessLevel: (overrideKey ? form.accessOverrides?.[overrideKey] : undefined) ?? form.accessLevel,
        isDefault: form.attributes.isDefault,
      })).filter((f) => !overrideKey || f.accessLevel !== 'NO_ACCESS'),
    }))
    .filter((tab) => !overrideKey || tab.forms.length > 0);
}

const EMPLOYEE_STATUS_BADGE_CLASS: Record<string, string> = {
  ACTIVE: 'statusBadgeActive',
  PENDING_CONTRACT: 'statusBadgeWarning',
  PENDING_ONBOARDING: 'statusBadgeInfo',
  ACTIVATION_PENDING: 'statusBadgeInfo',
  INACTIVE: 'statusBadgeInactive',
};

const EMPLOYEE_STATUS_LABEL_KEY: Record<string, [string, string]> = {
  ACTIVE: ['K_STATUS_ACTIVE', 'Active'],
  PENDING_CONTRACT: ['K_STATUS_PENDING_CONTRACT', 'Pending Contract'],
  PENDING_ONBOARDING: ['K_STATUS_PENDING_ONBOARDING', 'Pending Onboarding'],
  ACTIVATION_PENDING: ['K_STATUS_ACTIVATION_PENDING', 'Activation Pending'],
  INACTIVE: ['K_STATUS_INACTIVE', 'Inactive'],
};

// ============================================
// COMPONENT
// ============================================

interface EmployeeProfileViewProps {
  employeeId: string;
  /** When true, fetches and displays the tenant name in the profile card */
  showTenantInfo?: boolean;
  /** When true on first mount, opens the edit modal automatically (used by `?edit=true` from list pages). */
  initialEdit?: boolean;
}

function EmployeeProfileView({ employeeId, showTenantInfo, initialEdit = false }: EmployeeProfileViewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pathname = useLocation({ select: (l) => l.pathname });
  const setOverride = useBreadcrumbOverride((s) => s.setOverride);
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  // Tenant persona has no tenantData fetch (gated on showTenantInfo), so fall
  // back to the session's org id. Without this, tenant-side uploads persist
  // organisation_id=NULL and then disappear from /t/documents, which filters
  // on organisationId. Backend does not backfill.
  const sessionOrgId = useAuthStore((s) => s.session?.organization.id);

  const [isEditing, setIsEditing] = useState(initialEdit);
  const [editPending, setEditPending] = useState(false);
  const [showActivateConfirm, setShowActivateConfirm] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [showProvisionAccessConfirm, setShowProvisionAccessConfirm] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);

  const activateMutation = useMutation({
    mutationFn: () => activateEmployee(employeeId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['employee', employeeId] });
      void queryClient.invalidateQueries({ queryKey: ['allEmployees'] });
      void queryClient.invalidateQueries({ queryKey: ['employees'] });
      void queryClient.invalidateQueries({ queryKey: ['allEmployeeCounts'] });
      void queryClient.invalidateQueries({ queryKey: ['employeeCounts'] });
      setShowActivateConfirm(false);
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
      setShowActivateConfirm(false);
    },
  });

  const provisionAccessMutation = useMutation({
    mutationFn: () => provisionEmployeeAccess(employeeId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['employee', employeeId] });
      void queryClient.invalidateQueries({ queryKey: ['allEmployees'] });
      void queryClient.invalidateQueries({ queryKey: ['employees'] });
      void queryClient.invalidateQueries({ queryKey: ['allEmployeeCounts'] });
      void queryClient.invalidateQueries({ queryKey: ['employeeCounts'] });
      setShowProvisionAccessConfirm(false);
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({
        variant: 'critical',
        title: t(e.description, e.description),
      });
      setShowProvisionAccessConfirm(false);
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: () => deleteEmployee(employeeId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['employee', employeeId] });
      void queryClient.invalidateQueries({ queryKey: ['allEmployees'] });
      void queryClient.invalidateQueries({ queryKey: ['employees'] });
      void queryClient.invalidateQueries({ queryKey: ['allEmployeeCounts'] });
      void queryClient.invalidateQueries({ queryKey: ['employeeCounts'] });
      setShowDeactivateConfirm(false);
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
      setShowDeactivateConfirm(false);
    },
  });

  // Fetch employee data
  const {
    data: employeeData,
    isLoading,
    isError,
    error: employeeErrorObj,
  } = useQuery({
    queryKey: ['employee', employeeId],
    queryFn: () => getEmployeeById(employeeId),
  });
  const employeeForbidden = isForbiddenError(employeeErrorObj);

  // Fetch tenant info for admin view
  const { data: tenantData } = useQuery({
    queryKey: ['tenant', employeeData?.tenantId],
    queryFn: () => getTenantById(employeeData!.tenantId),
    enabled: !!showTenantInfo && !!employeeData?.tenantId,
  });

  // Avatar URL is denormalised onto the employee DTO (BE resolves
  // employee.user_id → users.profile_image_path → public URL).
  const linkedUserId = employeeData?.userId ?? null;

  // Fetch template snapshot for this employee
  const { data: snapshotData } = useQuery({
    queryKey: ['templateSnapshot', 'EMPLOYEE', employeeId],
    queryFn: () => getTemplateSnapshot('EMPLOYEE', employeeId),
    enabled: !!employeeId,
  });

  // Dynamic breadcrumb
  useEffect(() => {
    if (employeeData) {
      setOverride(pathname, getDisplayName(employeeData));
    }
    return () => setOverride(pathname, null);
  }, [employeeData, pathname, setOverride]);

  // Edit modal renders only when employee is loaded and not INACTIVE.
  // Computed at the boundary so `?edit=true` deep-link on an INACTIVE record
  // never flashes the modal open between mount and effect.
  const editModalOpen = isEditing && !!employeeData && employeeData.status !== 'INACTIVE';

  // Local tab state
  const [tabs, setTabs] = useState<ResolvedTab[]>([]);
  const [activeTabId, setActiveTabId] = useState('');
  const [initialized, setInitialized] = useState(false);

  const canViewDocuments = hasPermission('DOCUMENT_REQUESTS_VIEW');
  if (snapshotData?.tabs && !initialized) {
    const clientTabs = toResolvedTabs(snapshotData.tabs, showTenantInfo ? undefined : 'TENANT');
    setTabs(clientTabs);
    setActiveTabId(
      clientTabs[0]?._tabClientId ?? (canViewDocuments ? DOCUMENTS_TAB_ID : ''),
    );
    setInitialized(true);
  }
  if (!snapshotData?.tabs && snapshotData !== undefined && !initialized) {
    setActiveTabId(canViewDocuments ? DOCUMENTS_TAB_ID : '');
    setInitialized(true);
  }

  const activeTab = tabs.find((t) => t._tabClientId === activeTabId);
  const draggableItems = useMemo(() => toDraggableTabItems(tabs), [tabs]);

  const handleTabClick = useCallback((tab: TabItem) => {
    setActiveTabId(tab.id);
  }, []);

  const handleEditStart = useCallback(() => setIsEditing(true), []);
  const handleEditClose = useCallback(() => {
    setIsEditing(false);
    // Scrub `?edit=true` on close so refresh doesn't reopen the modal.
    if (initialEdit) {
      void navigate({
        to: '/w/employees/profile/$employeeId',
        params: { employeeId },
        search: {},
        replace: true,
      });
    }
  }, [initialEdit, navigate, employeeId]);
  const handleEditCancel = handleEditClose;
  const handleEditSaved = handleEditClose;

  const { countries } = useCountries();

  const countryName = useMemo(() => {
    if (!employeeData?.countryId) return '-';
    return countries.find((c) => c.id === employeeData.countryId)?.name ?? employeeData.countryId;
  }, [employeeData?.countryId, countries]);

  // Resolve employment type name via commercial terms — permission-aligned for both
  // admin (showTenantInfo=true) and tenant (showTenantInfo=false) callers.
  const { data: commercialTermsData } = useQuery({
    queryKey: ['commercialTerms', 'TENANT', employeeData?.tenantId],
    queryFn: () => getCommercialTerms('TENANT', employeeData!.tenantId, { size: 500 }),
    enabled: !!employeeData?.tenantId,
  });
  const employmentTypeName = useMemo(() => {
    if (!employeeData?.employmentTypeId) return '';
    for (const term of commercialTermsData?.content ?? []) {
      if (term.employmentType?.id === employeeData.employmentTypeId) return term.employmentType.name;
    }
    return '';
  }, [employeeData?.employmentTypeId, commercialTermsData]);

  const tenantName = showTenantInfo ? tenantData?.tenant.name : undefined;

  // Per BE V26: employees have no own profile_image_path column — they inherit
  // the linked user's avatar via employee.user_id → users.profile_image_path.
  // Save hits PUT /v1/users/{userId}/profile-image (USERS_UPDATE perm).
  const handleProfileImageSave = useCallback(
    async (blob: Blob) => {
      if (!linkedUserId) {
        throw new Error('This employee has no linked user account yet.');
      }
      const file = new File([blob], `profile-${linkedUserId}-${Date.now()}.jpg`, {
        type: blob.type || 'image/jpeg',
      });
      const uploadStarted = performance.now();
      const uploaded = await uploadFile(file, {
        workflow: 'PROFILE_IMAGE',
        objectType: 'USER',
        objectId: linkedUserId,
      });
      if (!uploaded.bucketPath) {
        throw new Error(
          'Upload response missing bucketPath — file-management-service may not be running ' +
            'a recent enough build, or the PROFILE_IMAGE workflow is not configured to use the public bucket.',
        );
      }
      const linkStarted = performance.now();
      const updated = await setUserProfileImage(linkedUserId, uploaded.bucketPath);
      console.log('[ProfileImage upload] employee (via linked user)', {
        employeeId,
        linkedUserId,
        uploadedFileId: uploaded.id,
        sizeKb: (uploaded.size / 1024).toFixed(1),
        bucketPath: uploaded.bucketPath,
        uploadMs: Math.round(linkStarted - uploadStarted),
        linkMs: Math.round(performance.now() - linkStarted),
        finalProfileImageUrl: updated.profileImageUrl,
      });
      void queryClient.invalidateQueries({ queryKey: ['employee', employeeId] });
      void queryClient.invalidateQueries({ queryKey: ['allEmployees'] });
      void queryClient.invalidateQueries({ queryKey: ['employees'] });
      addNotification({
        variant: 'success',
        title: t('K_PROFILE_IMAGE_SAVED', 'Profile photo updated.'),
      });
    },
    [linkedUserId, employeeId, queryClient, addNotification, t],
  );

  const handleProfileImageRemove = useCallback(async () => {
    if (!linkedUserId) return;
    await clearUserProfileImage(linkedUserId);
    void queryClient.invalidateQueries({ queryKey: ['employee', employeeId] });
    void queryClient.invalidateQueries({ queryKey: ['allEmployees'] });
    addNotification({
      variant: 'success',
      title: t('K_PROFILE_IMAGE_REMOVED', 'Profile photo removed.'),
    });
  }, [linkedUserId, employeeId, queryClient, addNotification, t]);

  const canEditEmployeeAvatar = hasPermission('USERS_UPDATE') && !!linkedUserId;

  const profileFields = useMemo<ProfileField[]>(
    () =>
      employeeData ? buildProfileFields(employeeData, countryName, employmentTypeName, t, tenantName) : [],
    [employeeData, countryName, employmentTypeName, t, tenantName],
  );

  if (isLoading) {
    return (
      <div className={styles.page}>
        <ProfileCard name="" fields={[]} loading />
        <div className={styles.loadingState}>{t('K_LOADING_EMPLOYEE_PROFILE', 'Loading employee profile...')}</div>
      </div>
    );
  }

  // --- Forbidden (page-level — detail route can't render anything without the employee) ---
  if (employeeForbidden) {
    const backTo = showTenantInfo ? '/w/employees' : '/t/employees';
    return (
      <div className={styles.page}>
        <Forbidden
          variant="page"
          onAction={() => {
            void navigate({ to: backTo });
          }}
          actionLabel={t('K_BUTTON_GO_BACK', 'Go Back')}
        />
      </div>
    );
  }

  if (isError || !employeeData) {
    return (
      <div className={styles.page}>
        <div className={styles.errorState}>
          <p>{t('K_FAILED_LOAD_EMPLOYEE_PROFILE', 'Failed to load employee profile.')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <ProfileCard
        name={getDisplayName(employeeData)}
        fields={profileFields}
        avatarColor="blue"
        columns={2}
        editableAvatar={{
          currentImageUrl: employeeData.profileImageUrl ?? null,
          canEdit: canEditEmployeeAvatar,
          onSave: handleProfileImageSave,
          onRemove: handleProfileImageRemove,
          captureMode: 'user',
        }}
        action={
          <div className={styles.actionGroup}>
            {(() => {
              const status = employeeData.status;
              const badgeClass = styles[EMPLOYEE_STATUS_BADGE_CLASS[status] ?? 'statusBadgeInactive'] ?? '';
              const labelEntry = EMPLOYEE_STATUS_LABEL_KEY[status];
              const label = labelEntry ? t(labelEntry[0], labelEntry[1]) : toTitleCase(status);
              return <span className={`${styles.statusBadge} ${badgeClass}`}>{label}</span>;
            })()}
            <ProfileActionsMenu
              actions={(() => {
              const items: ProfileAction[] = [];
              if (hasPermission('EMPLOYEES_UPDATE') && employeeData.status !== 'INACTIVE') {
                items.push({ id: 'edit', label: t('K_EDIT', 'Edit'), onClick: handleEditStart });
              }
              if (hasPermission('EMPLOYEES_UPDATE') && showTenantInfo && employeeData.status === 'PENDING_ONBOARDING') {
                items.push({
                  id: 'sendOnboardingLink',
                  label: t('K_BUTTON_SEND_ONBOARDING_LINK', 'Send Onboarding Link'),
                  onClick: () => setShowProvisionAccessConfirm(true),
                });
              }
              if (hasPermission('EMPLOYEES_UPDATE') && showTenantInfo && (employeeData.status === 'PENDING_ONBOARDING' || employeeData.status === 'ACTIVATION_PENDING')) {
                items.push({ id: 'activate', label: t('K_BUTTON_ACTIVATE', 'Activate'), onClick: () => setShowActivateConfirm(true), variant: 'primary' });
              }
              // Workpay-persona "Generate Contract" — only shown when viewing from the
              // workpay admin surface (showTenantInfo=true), gated on ENVELOPES_CREATE and
              // PENDING_CONTRACT status. Once the contract-signed webhook has fired the
              // employee has left PENDING_CONTRACT and a new envelope would bypass the
              // lifecycle (Keycloak provision + onboarding email already sent).
              if (showTenantInfo && hasPermission('ENVELOPES_CREATE') && employeeData.status === 'PENDING_CONTRACT') {
                items.push({
                  id: 'generateContract',
                  label: t('K_DS_ACTION_GENERATE_CONTRACT', 'Generate Contract'),
                  onClick: () => {
                    void navigate({
                      to: '/w/signing-requests/new',
                      search: { employeeId: employeeData.id },
                    });
                  },
                  variant: 'primary',
                });
              }
              if (showTenantInfo && employeeData.tenantId) {
                items.push({
                  id: 'viewTenant',
                  label: t('K_BUTTON_VIEW_TENANT', 'View Client'),
                  onClick: () => {
                    void navigate({ to: '/w/dashboard/profile/$tenantId', params: { tenantId: employeeData.tenantId } });
                  },
                });
              }
              if (showTenantInfo && employeeData.legalEntity) {
                items.push({
                  id: 'viewLegalEntity',
                  label: t('K_BUTTON_VIEW_LEGAL_ENTITY', 'View Legal Entity'),
                  onClick: () => {
                    void navigate({ to: '/w/entities/profile/$entityId', params: { entityId: employeeData.legalEntity!.id } });
                  },
                });
              }
              // Workpay-persona only — tenants don't have EMPLOYEES_DELETE in scope
              if (showTenantInfo && hasPermission('EMPLOYEES_DELETE') && employeeData.status === 'ACTIVE') {
                items.push({ id: 'deactivate', label: t('K_DEACTIVATE', 'Deactivate'), onClick: () => setShowDeactivateConfirm(true) });
              }
              if (hasPermission('AUDIT_READ')) {
                items.push({ id: 'audit', label: t('K_AUDIT_TITLE', 'Audit trail'), onClick: () => setAuditOpen(true) });
              }
              return items;
            })()}
            />
          </div>
        }
      />

      {/* Edit Modal */}
      <Modal
        isOpen={editModalOpen}
        onClose={handleEditCancel}
        title={t('K_MODAL_EDIT_EMPLOYEE', 'Edit Employee')}
        size="lg"
        footer={
          <div className={styles.editModalFooter}>
            <Button variant="outline" size="sm" onClick={handleEditCancel} disabled={editPending}>
              {t('K_CANCEL', 'Cancel')}
            </Button>
            <Button variant="filled" size="sm" type="submit" form="employee-edit-form" disabled={editPending}>
              {editPending ? t('K_BUTTON_SAVING', 'Saving...') : t('K_SAVE', 'Save')}
            </Button>
          </div>
        }
      >
        <EmployeeEditForm
          employeeData={employeeData}
          formId="employee-edit-form"
          onSaved={handleEditSaved}
          onPendingChange={setEditPending}
        />
      </Modal>

      <AuditTrailModal
        open={auditOpen}
        onClose={() => setAuditOpen(false)}
        filter={{ resourceType: AUDIT_RESOURCE.EMPLOYEE, resourceId: employeeId }}
        subject={getDisplayName(employeeData) || undefined}
      />

      {/* Activate employee confirmation modal */}
      <AlertModal
        variant="warning"
        title={t('K_MODAL_ACTIVATE_EMPLOYEE', 'Activate Employee')}
        description={t('K_CONFIRM_ACTIVATE_EMPLOYEE', 'Are you sure you want to activate "{name}"? This will change the employee status to Active.').replace('{name}', getDisplayName(employeeData))}
        isOpen={showActivateConfirm}
        onClose={() => setShowActivateConfirm(false)}
        primaryLabel={t('K_BUTTON_ACTIVATE', 'Activate')}
        onPrimaryClick={() => activateMutation.mutate()}
        loading={activateMutation.isPending}
      />

      {/* Send onboarding link confirmation modal — provisions Keycloak user (first call)
          or re-issues the email (subsequent calls); prior email link is invalidated. */}
      <AlertModal
        variant="warning"
        title={t('K_MODAL_SEND_ONBOARDING_LINK', 'Send Onboarding Link')}
        description={t(
          'K_CONFIRM_SEND_ONBOARDING_LINK',
          'Send the onboarding link to "{name}"? They will receive an email with a link to set their password and finish onboarding. Any previous link will be invalidated.',
        ).replace('{name}', getDisplayName(employeeData))}
        isOpen={showProvisionAccessConfirm}
        onClose={() => setShowProvisionAccessConfirm(false)}
        primaryLabel={t('K_BUTTON_SEND_ONBOARDING_LINK', 'Send Onboarding Link')}
        onPrimaryClick={() => provisionAccessMutation.mutate()}
        loading={provisionAccessMutation.isPending}
      />

      {/* Deactivate employee confirmation modal */}
      <AlertModal
        variant="danger"
        title={t('K_MODAL_DEACTIVATE_EMPLOYEE', 'Deactivate Employee')}
        description={t('K_CONFIRM_DEACTIVATE_EMPLOYEE', 'Are you sure you want to deactivate "{name}"? The employee will no longer be active.').replace('{name}', getDisplayName(employeeData))}
        isOpen={showDeactivateConfirm}
        onClose={() => setShowDeactivateConfirm(false)}
        primaryLabel={t('K_DEACTIVATE', 'Deactivate')}
        onPrimaryClick={() => deactivateMutation.mutate()}
        loading={deactivateMutation.isPending}
      />

      {/* Tabs — Documents static tab always visible; form tabs appear only when the snapshot has some. */}
      <div className={styles.tabBar}>
        {hasPermission('DOCUMENT_REQUESTS_VIEW') && (
          <button
            type="button"
            className={`${styles.staticTab} ${activeTabId === DOCUMENTS_TAB_ID ? styles.staticTabActive : ''}`}
            onClick={() => setActiveTabId(DOCUMENTS_TAB_ID)}
          >
            {t('K_TAB_DOCUMENTS', 'Documents')}
          </button>
        )}
        {tabs.length > 0 && (
          <DraggableTabs
            tabs={draggableItems}
            activeTabId={activeTabId}
            onTabClick={handleTabClick}
            onReorder={() => {}}
            draggable={false}
            removable={false}
          />
        )}
      </div>

      <div className={styles.tabContent}>
        {activeTabId === DOCUMENTS_TAB_ID && hasPermission('DOCUMENT_REQUESTS_VIEW') ? (
          <EmployeeDocumentsTab
            employeeId={employeeId}
            organisationId={showTenantInfo ? tenantData?.organization?.id : sessionOrgId ?? undefined}
            canOpenSigningRequestDetail={!!showTenantInfo}
          />
        ) : activeTab ? (
          <TabFormsContent
            forms={activeTab.forms}
            mode={showTenantInfo ? { kind: 'admin', tenantId: employeeId } : { kind: 'tenant', tenantId: employeeId }}
            objectType="EMPLOYEE"
            objectId={employeeId}
          />
        ) : (
          <p className={styles.tabContentPlaceholder}>
            {t('K_SELECT_TAB_PLACEHOLDER', 'Select a tab to view its forms.')}
          </p>
        )}
      </div>
    </div>
  );
}

export default EmployeeProfileView;
